import React from "react";
import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  Search,
  XCircle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useEffect, useMemo, useState } from "react";
import { changeTransactionStatusApi, getTransactionHistory } from "../api/api";

type PaymentStatus = "pending" | "success" | "failed" | "partially-failed" | "partial_failed" | "partially failed";
type StatusFilter = "select" | "All" | PaymentStatus;

type Beneficiary = {
  beneficiaries_id: number;
  customer_id: number;
  bank_name?: string;
  account_holder_name?: string;
  ifsc_code?: string;
  account_number?: string;
  verified?: number;
  active_status?: number;
  customer?: {
    mobile_number?: string;
    customer_name?: string;
  };
};

type RetailerInfo = {
  name?: string;
  mobile_number?: string;
};

type Transaction = {
  transactions_id: number;
  beneficiary_id?: number;
  transaction_id?: string | null;
  user_amount: string;
  user_amount_with_charge: string;
  transaction_status: PaymentStatus;
  transaction_date: string;
  verified_id?: number | null;
  verified_type?: string | null;
  beneficiary?: Beneficiary;
  retailer?: RetailerInfo;
};

// How many records to fetch per API call — increase as needed
const PAGE_SIZE = 100;

const s: Record<string, React.CSSProperties> = {
  sectionHeader: {
    background: "#4a6b8a",
    color: "#fff",
    borderRadius: 8,
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 500,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  exportBtn: {
    fontSize: 12,
    background: "rgba(255,255,255,0.15)",
    border: "none",
    color: "#fff",
    padding: "4px 12px",
    borderRadius: 6,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 16,
  },
  inputWrap: {
    position: "relative",
    flex: 1,
    minWidth: 160,
  },
  inputIcon: {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6b7280",
    pointerEvents: "none",
  },
  formInput: {
    height: 36,
    width: "100%",
    paddingLeft: 32,
    paddingRight: 10,
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 13,
    background: "#fff",
    color: "#111",
    outline: "none",
  },
  select: {
    height: 36,
    padding: "0 10px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 13,
    background: "#fff",
    color: "#111",
    outline: "none",
    minWidth: 130,
  },
  dateInput: {
    height: 36,
    padding: "0 8px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 13,
    background: "#fff",
    color: "#111",
    outline: "none",
    minWidth: 130,
  },
  searchBtn: {
    height: 36,
    padding: "0 18px",
    background: "#2cc7a5",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "14px 16px",
    marginBottom: 10,
  },
  grid4: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 12,
    alignItems: "start",
  },
  label: {
    fontSize: 11,
    color: "#6b7280",
    marginBottom: 3,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  val: {
    fontSize: 13,
    color: "#111827",
    fontWeight: 500,
  },
  sub: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 2,
  },
  divider: {
    borderTop: "1px solid #f3f4f6",
    marginTop: 12,
    paddingTop: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  txnInput: {
    width: 180,
    padding: "4px 8px",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    fontSize: 12,
    background: "#f9fafb",
    outline: "none",
    color: "#111",
  },
  approveBtn: {
    padding: "5px 14px",
    borderRadius: 8,
    fontSize: 12,
    cursor: "pointer",
    fontWeight: 500,
    border: "1px solid #059669",
    background: "transparent",
    color: "#059669",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  rejectBtn: {
    padding: "5px 14px",
    borderRadius: 8,
    fontSize: 12,
    cursor: "pointer",
    fontWeight: 500,
    border: "1px solid #DC2626",
    background: "transparent",
    color: "#DC2626",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 12,
    fontSize: 13,
    color: "#6b7280",
  },
  pageBtn: {
    height: 30,
    minWidth: 30,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 13,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyCard: {
    textAlign: "center",
    padding: 32,
    color: "#6b7280",
    fontSize: 13,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
  },
};

const DISPLAY_PER_PAGE = 10; // records shown per page in UI

function StatusBadge({ status }: { status: PaymentStatus | string }) {
  const normalized = String(status).toLowerCase();
  const isPartial = normalized.includes("partial") || normalized.includes("partially");
  const bg =
    normalized === "success"
      ? "#D1FAE5"
      : normalized === "failed" || isPartial
      ? "#FECACA"
      : "#FEF3C7";
  const color =
    normalized === "success"
      ? "#065F46"
      : normalized === "failed" || isPartial
      ? "#7F1D1D"
      : "#92400E";

  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 700,
        background: bg,
        color,
      }}
    >
      {String(status).toUpperCase()}
    </span>
  );
}

function StatCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div
      style={{
        minWidth: 150,
        flex: 1,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: "16px 18px",
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: "#6b7280",
          marginBottom: 4,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || "#111827" }}>{value}</div>
    </div>
  );
}

function formatAccountNumber(value?: string) {
  if (!value) return "—";
  const digits = value.replace(/\D/g, "");
  if (!digits) return "—";
  const parts = digits.match(/.{1,4}/g) || [digits];
  return parts.join(" ");
}

function formatDmy(value?: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

export default function ApprovePaymentPage() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("select");
  const [uiPage, setUiPage] = useState(1); // client-side page for display
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [txnNumbers, setTxnNumbers] = useState<Record<number, string>>({});
  const [confirmAction, setConfirmAction] = useState<{ transaction: Transaction; status: PaymentStatus } | null>(null);

  const getTxnNumber = (t: Transaction) =>
    txnNumbers[t.transactions_id] ?? t.transaction_id ?? "";

  const handleTxnNumberChange = (id: number, value: string) =>
    setTxnNumbers((prev) => ({ ...prev, [id]: value }));

  // ── Fetch ALL records at once (large per_page) ─────────────────────────────
  const fetchTransactions = async () => {
    console.log("=== FETCH TRANSACTIONS STARTED ===");
    console.log("User:", user);
    console.log("Token available:", !!user?.token);
    
    if (!user?.token) { 
      console.error("No token available");
      setError("Not authenticated"); 
      return; 
    }
    
    setLoading(true);
    setError(null);
    try {
      const payload: any = {
        login_id: (user as any).id ?? 0,
        page: 1,
        per_page: PAGE_SIZE,
        search: query,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
      };
      if (statusFilter === "All") {
        payload.status = "All Status";
      } else if (statusFilter !== "select") {
        payload.status = statusFilter;
      }
      
      console.log("Fetching with payload:", payload);
      const body = await getTransactionHistory(payload, user.token);
      console.log("API Response:", body);

      if (!body?.success) throw new Error(body?.message || "Failed to fetch transactions");

      const data = body.data;
      const fetchedTransactions: Transaction[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.transactions)
        ? data.transactions
        : [];

      console.log("Parsed transactions:", fetchedTransactions);
      setTransactions(fetchedTransactions);
      setUiPage(1);
    } catch (err: any) {
      console.error("Fetch error:", err);
      setError(err.message || "Unable to load transactions");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    console.log("=== USEEFFECT TRIGGERED ===");
    console.log("Current user:", user);
    if (user?.token) {
      fetchTransactions(); 
    }
  }, [user?.token, user]);

  // ── Status change ──────────────────────────────────────────────────────────
  const handleStatusChange = async (t: Transaction, status: PaymentStatus) => {
    if (!user?.token) { setError("Not authenticated"); return; }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        login_id: (user as any).id ?? 0,
        transactions_id: t.transactions_id,
        transaction_status: status,
        transaction_no: getTxnNumber(t) || `TXN${t.transactions_id}`,
      };
      const body = await changeTransactionStatusApi(payload, user.token);
      if (!body?.success) throw new Error(body?.message || "Failed to update transaction status");

      // refresh the current list from server immediately after status change
      await fetchTransactions();
      // notify other pages (like dashboard) to refresh summary widgets
      window.dispatchEvent(new Event("walletUpdated"));
    } catch (err: any) {
      setError(err.message || "Unable to update transaction status");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const rows = [
      ["Transaction ID", "Date", "Sender", "Retailer", "Beneficiary", "Bank", "Account", "Amount", "With Charge", "Status"],
      ...filtered.map((t) => [
        t.transaction_id ?? "",
        t.transaction_date ?? "",
        t.beneficiary?.customer?.mobile_number ?? "",
        t.retailer?.name ?? "",
        t.beneficiary?.account_holder_name ?? "",
        t.beneficiary?.bank_name ?? "",
        t.beneficiary?.account_number ?? "",
        t.user_amount ?? "",
        t.user_amount_with_charge ?? "",
        t.transaction_status ?? "",
      ]),
    ];

    const csv = rows
      .map((row) =>
        row
          .map((cell) => String(cell ?? "").replace(/\n/g, " ").replace(/"/g, '""'))
          .map((cell) => `"${cell}"`)
          .join(",")
      )
      .join("\n");

    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = "approve_payment_export.csv";
    a.click();
  };

  // ── Client-side filter (on all fetched records) ────────────────────────────
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return transactions.filter((t) => {
      const matchSearch =
        !q ||
        String(t.transactions_id).includes(q) ||
        (t.transaction_id ?? "").toLowerCase().includes(q) ||
        (t.retailer?.name ?? "").toLowerCase().includes(q) ||
        (t.retailer?.mobile_number ?? "").toLowerCase().includes(q) ||
        (t.beneficiary?.account_holder_name ?? "").toLowerCase().includes(q) ||
        (t.beneficiary?.bank_name ?? "").toLowerCase().includes(q) ||
        (t.beneficiary?.account_number ?? "").toLowerCase().includes(q) ||
        t.user_amount.includes(q) ||
        t.transaction_status.includes(q);
      const matchStatus = statusFilter === "select" || statusFilter === "All" || t.transaction_status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [transactions, query, statusFilter]);

  const totalCount = filtered.length;
  const successCount = filtered.filter((t) => t.transaction_status === "success").length;
  const failedCount = filtered.filter((t) => t.transaction_status === "failed").length;
  const pendingCount = filtered.filter((t) => t.transaction_status === "pending").length;
  const partialFailedCount = filtered.filter((t) =>
    String(t.transaction_status).toLowerCase().includes("partial")
  ).length;

  // ── UI Pagination (client-side on filtered records) ────────────────────────
  const totalPages = Math.max(1, Math.ceil(filtered.length / DISPLAY_PER_PAGE));
  const currentPage = Math.min(uiPage, totalPages);
  const pageStart = (currentPage - 1) * DISPLAY_PER_PAGE;
  const visible = filtered.slice(pageStart, pageStart + DISPLAY_PER_PAGE);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <h1>Approve Payment</h1>
        <p>Welcome to Money Transfer Portal</p>
      </div>

      {error && <div style={{ color: "#d9534f", marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {/* Header */}
      <div style={s.sectionHeader}>
        <span>DMT Transaction</span>
        <button style={s.exportBtn} onClick={handleExport} disabled={exporting}>
          <FileSpreadsheet size={13} /> {exporting ? "Exporting..." : "Export to Excel"}
        </button>
      </div>

      {/* Filters */}
      <div style={s.toolbar}>
        <div style={s.inputWrap}>
          <span style={s.inputIcon}><Search size={14} /></span>
          <input
            style={s.formInput}
            placeholder="Search by ID, name, bank, amount…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setUiPage(1); }}
          />
        </div>
        <input type="date" style={s.dateInput} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        <input type="date" style={s.dateInput} value={toDate} onChange={(e) => setToDate(e.target.value)} />
        <select style={s.select}>
          <option>All Method</option>
          <option>IMPS</option>
          <option>NEFT</option>
          <option>RTGS</option>
        </select>
        <select
          style={s.select}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as StatusFilter);
            setUiPage(1);
          }}
        >
          <option value="select">Select Status</option>
          <option value="All">All Status</option>
          <option value="pending">Pending</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>
        <button style={s.searchBtn} onClick={fetchTransactions}>
          <Search size={14} /> Search
        </button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <StatCard label="Total transactions" value={totalCount} />
        <StatCard label="Successful" value={successCount} color="#15803d" />
        <StatCard label="Failed" value={failedCount} color="#b91c1c" />
        <StatCard label="Pending" value={pendingCount} color="#92400E" />
        {partialFailedCount > 0 && <StatCard label="Partial failed" value={partialFailedCount} color="#d97706" />}
      </div>

      {/* Cards */}
      {loading ? (
        <div style={{ padding: 24, textAlign: "center", color: "#6b7280", fontSize: 13 }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={s.emptyCard}>
          {transactions.length === 0 ? "No payment requests found. Try adjusting filters." : "No results match your search."}
          <div style={{ marginTop: 12, fontSize: 12, color: "#9ca3af" }}>
            Total transactions fetched: {transactions.length}
          </div>
        </div>
      ) : (
        visible.map((t) => {
          const isPartialFailed = String(t.transaction_status).toLowerCase().includes("partial");

          return (
          <div
            key={t.transactions_id}
            style={{
              ...s.card,
              background: isPartialFailed ? "#f33f3f" : "#fff",
              borderColor: isPartialFailed ? "#db3333" : "#e5e7eb",
            }}
          >
            <div style={s.grid4}>
              {/* Col 1 — Date & IDs */}
              <div>
                <div style={s.label}>Date</div>
                <div style={s.val}>{formatDmy(t.transaction_date)}</div>
                <div style={s.sub}>ID: #{t.transactions_id}</div>
                {t.transaction_id && (
                  <div style={{ ...s.sub, wordBreak: "break-all" }}>TXN: {t.transaction_id}</div>
                )}
                {t.verified_type && (
                  <div style={s.sub}>Verified by: {t.verified_type}</div>
                )}
              </div>

              {/* Col 2 — Sender */}
              <div>
                <div style={s.label}>Sender</div>
                <div style={{ ...s.val, fontSize: 15, fontWeight: 700 }}>{t.beneficiary?.customer?.mobile_number ?? "—"}</div>
                <div style={{ ...s.sub, fontSize: 13, fontWeight: 600 }}>{t.beneficiary?.customer?.customer_name ?? "—"}</div>
              </div>

              {/* Col 3 — Retailer */}
              <div>
                <div style={{ ...s.label, fontWeight: 700 }}>Retailer</div>
                <div style={{ ...s.val, fontSize: 15, fontWeight: 700 }}>
                  {t.retailer?.name ?? "—"}
                </div>
                <div style={{ ...s.sub, fontSize: 13, fontWeight: 700 }}>
                  {t.retailer?.mobile_number ?? "—"}
                </div>
              </div>

              {/* Col 4 — Beneficiary */}
              <div>
                <div style={s.label}>Beneficiary</div>
                <div style={{ ...s.val, fontSize: 15, fontWeight: 700 }}>{t.beneficiary?.account_holder_name ?? "—"}</div>
                <div style={{ ...s.sub, fontSize: 13, fontWeight: 600 }}>Bank: {t.beneficiary?.bank_name ?? "—"}</div>
                <div style={{ ...s.sub, fontSize: 18, fontWeight: 500, color: "#111827", marginTop: 6 }}>A/C: {formatAccountNumber(t.beneficiary?.account_number)}</div>
                <div style={{ ...s.sub, fontSize: 18, fontWeight: 500, color: "#111827", marginTop: 2 }}>A/C: {t.beneficiary?.account_number}</div>
                <div style={{ ...s.sub, fontSize: 13, fontWeight: 600 }}>IFSC: {t.beneficiary?.ifsc_code ?? "—"}</div>
              </div>

              {/* Col 5 — Amount */}
              <div>
                <div style={s.label}>Amount</div>
                <div style={{ ...s.val, fontSize: 22, fontWeight: 700 }}>₹ {t.user_amount}</div>
                <div style={{ ...s.sub, fontWeight: 700 }}>With Charge: ₹ {t.user_amount_with_charge}</div>
                <div style={{ marginTop: 10 }}>
                  <div style={s.label}>Status</div>
                  <div style={{ marginTop: 4 }}>
                    <StatusBadge status={t.transaction_status} />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom row */}
            <div style={s.divider}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "#6b7280" }}>Transaction No:</span>
                <input
                  type="text"
                  style={s.txnInput}
                  placeholder="Enter txn no."
                  value={String(getTxnNumber(t))}
                  onChange={(e) => handleTxnNumberChange(t.transactions_id, e.target.value)}
                />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={{ ...s.approveBtn, opacity: loading ? 0.5 : 1 }}
                  disabled={loading}
                  onClick={() => setConfirmAction({ transaction: t, status: "success" })}
                >
                  <CheckCircle size={14} /> Approve
                </button>
                <button
                  style={{ ...s.rejectBtn, opacity: loading ? 0.5 : 1 }}
                  disabled={loading}
                  onClick={() => setConfirmAction({ transaction: t, status: "failed" })}
                >
                  <XCircle size={14} /> Reject
                </button>
              </div>
            </div>
          </div>
          );
        })
      )}

      {/* Pagination */}
      <div style={s.pagination}>
        <span>
          Showing {filtered.length === 0 ? 0 : pageStart + 1}–{Math.min(pageStart + DISPLAY_PER_PAGE, filtered.length)} of {filtered.length}
        </span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            style={{ ...s.pageBtn, opacity: currentPage === 1 ? 0.4 : 1 }}
            disabled={currentPage === 1}
            onClick={() => setUiPage((v) => Math.max(1, v - 1))}
          >
            <ChevronLeft size={15} />
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            style={{ ...s.pageBtn, opacity: currentPage === totalPages ? 0.4 : 1 }}
            disabled={currentPage === totalPages}
            onClick={() => setUiPage((v) => Math.min(totalPages, v + 1))}
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {confirmAction && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: 360,
              maxWidth: "90vw",
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
              {confirmAction.status === "success" ? "Approve payment?" : "Reject payment?"}
            </div>
            <div style={{ fontSize: 13, color: "#374151" }}>
              Do you want to {confirmAction.status === "success" ? "approve" : "reject"} this payment of ₹ {confirmAction.transaction.user_amount}?
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                style={{ ...s.pageBtn, width: "auto", padding: "0 16px" }}
                onClick={() => setConfirmAction(null)}
              >
                No
              </button>
              <button
                style={confirmAction.status === "success" ? s.approveBtn : s.rejectBtn}
                onClick={() => {
                  const { transaction, status } = confirmAction;
                  setConfirmAction(null);
                  handleStatusChange(transaction, status);
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}